import { Button, Collapse, Group, Paper, Select } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCaretDown, IconCaretRight } from "@tabler/icons-react";
import { useLocation } from "react-router";
import { useToast } from "../../../mantine/notifications/useToast";
import type { components } from "../../../types/api.types";
import type { Person } from "../../../utils/backend-types";
import { ObjectUrlName } from "../../../utils/enums";
import type {
  ChangeableFieldData,
  ChangeableFieldWidgetOptions
} from "../../ChangeableFieldWidget/ChangeableFieldWidget";
import { ChangeableFieldWidget } from "../../ChangeableFieldWidget/ChangeableFieldWidget";
import { ChangeableOperatorHistory } from "./ChangeableOperatorHistory";
import { convertChangeableFieldDataToOperationPeriods } from "./utils/convertChangeableFieldDataToOperationPeriods";
import { convertOperatorsToChangeableFieldData } from "./utils/convertOperatorsToChangeableFieldData";

const changeableOptions = (isGenerator: boolean) => {
  return {
    addButtonText: `neuen ${isGenerator ? "Betreiber" : "Mieter"} eintragen`,
    startDateHelpText: `Bitte erfassen Sie melderelevante ${isGenerator ? "Betreiberwechsel" : "Mieterwechsel"} nach dem 01.01.2020 hier.`,
    endDatePlaceholder: "Aktuell",
    deleteTooltipText: `${isGenerator ? "Betreiberwechsel" : "Mieterwechsel"} löschen`,
    saveButtonText: `${isGenerator ? "Betreiberwechsel" : "Mieterwechsel"} übernehmen`
  } satisfies ChangeableFieldWidgetOptions;
};

export type OperationPeriod = components["schemas"]["OperationPeriod"];

export interface ChangeableOperatorProps {
  changeableOperatorHistory?: Array<OperationPeriod>;
  isCombined?: boolean;
  isPartialFeedInSite?: boolean;
  isOnboardingDone?: boolean;
  persons: Array<Person>;
  onChange?: (changeableFieldData: Array<OperationPeriod>) => void;
  onError?: (error: string | undefined) => void;
}

function ChangeableOperator({
  changeableOperatorHistory,
  isCombined: isCombinedGenerator,
  isPartialFeedInSite,
  isOnboardingDone,
  persons,
  onChange,
  onError
}: ChangeableOperatorProps) {
  const [
    isHistoryCollapseOpen,
    { toggle: toggleHistory, close: closeHistory }
  ] = useDisclosure();
  const [isCollapseOpen, { open: openCollapse, close: closeCollapse }] =
    useDisclosure();

  const { showToast } = useToast();
  const location = useLocation();
  const isGenerator = location.pathname.includes(ObjectUrlName.Generators);

  const needsConfirmation = isPartialFeedInSite && isOnboardingDone;

  function handleClickHistoryButton() {
    closeCollapse();
    toggleHistory();
  }

  function handleClickEditButton() {
    closeHistory();
    openCollapse();
  }

  function handleSaveOperators(
    changeableFieldData: Array<ChangeableFieldData<number | null>>
  ) {
    if (changeableFieldData.some((data) => data.value === null)) {
      onError?.("Bitte wählen Sie ein Unternehmen aus.");
      return Promise.resolve();
    }
    const dataForBE =
      convertChangeableFieldDataToOperationPeriods(changeableFieldData);

    if (needsConfirmation) {
      showToast({
        type: "warning",
        message:
          "Sie haben eine Änderung im Feld „Unternehmen“ vorgenommen. Bitte denken Sie daran, dass Sie bei einem Wechsel des Betreibers diese Änderungen in den Verträgen berücksichtigen müssen, indem Sie neue Verträge erstellen."
      }); // this could probably be prettier
    }

    onChange?.(dataForBE);
    closeCollapse();
    return Promise.resolve(); // Promise because ChangeableFieldWidget expects to have a save function that returns a Promise here
  }

  function getConfirmationOnSaveElement() {
    if (isCombinedGenerator) {
      return (
        <>
          <p>
            Dieser Erzeuger ist Teil einer stromsteuerlichen Anlage, die weitere
            Erzeuger umfasst. Wenn Sie den Betreiberwechsel bestätigen, wird
            dieser für alle Erzeuger, die Teil der stromsteuerlichen Anlage
            sind, durchgeführt. Soll der Betreiberwechsel nicht für alle
            Erzeuger der stromsteuerlichen Anlage durchgeführt werden, brechen
            Sie den Vorgang bitte zunächst ab und lösen Sie die/den Erzeuger,
            für den/die der Wechsel durchgeführt werden soll, aus der
            stromsteuerlichen Anlage heraus und führen Sie den Betreiberwechsel
            danach für die betreffenden Erzeuger durch.
          </p>
          <p>
            Soll der Betreiberwechsel trotzdem für alle Erzeuger der
            stromsteuerlichen Anlage durchgeführt werden?
          </p>
        </>
      );
    }

    return null;
  }

  const confirmationOnSaveElement = getConfirmationOnSaveElement();
  const suggestedChangeableOperators = convertOperatorsToChangeableFieldData(
    changeableOperatorHistory
  );

  if (!changeableOperatorHistory) {
    return null;
  }

  const currentOperatorName = persons?.find(
    (person) => person.id === suggestedChangeableOperators?.at(-1)?.value
  )?.name;

  return (
    <div className="ChangeableOperator FormField">
      <Group mb="xs">
        <Button
          flex={1}
          justify="flex-start"
          leftSection={
            isHistoryCollapseOpen ? (
              <IconCaretDown strokeWidth="1" />
            ) : (
              <IconCaretRight strokeWidth="1" />
            )
          }
          variant="default"
          onClick={handleClickHistoryButton}
        >
          {currentOperatorName}
        </Button>
        <Button
          color="primary"
          disabled={isCollapseOpen}
          onClick={handleClickEditButton}
        >
          {`${isGenerator ? "Betreiberwechsel" : "Mieterwechsel"} bearbeiten`}
        </Button>
      </Group>

      <Collapse expanded={isHistoryCollapseOpen}>
        <ChangeableOperatorHistory
          companies={persons}
          operators={changeableOperatorHistory}
        />
      </Collapse>

      <Collapse expanded={isCollapseOpen}>
        <Paper>
          {suggestedChangeableOperators && persons && (
            <ChangeableFieldWidget
              confirmationOnSaveCancellable={!needsConfirmation}
              confirmationOnSaveElement={confirmationOnSaveElement}
              dataName="Betreiberwechseln"
              initialValues={suggestedChangeableOperators}
              options={changeableOptions(isGenerator)}
              valueColumns={[
                {
                  header: "Unternehmen*",
                  accessorKey: "value",
                  Cell: (props, onChange) => {
                    return (
                      <Select
                        data={persons.map((person) => ({
                          value: person.id.toString(),
                          label: person.name
                        }))}
                        placeholder="Unternehmen auswählen"
                        value={String(props.cell.getValue())}
                        onChange={(newValue) => {
                          onChange(props.row.index, Number(newValue));
                        }}
                      />
                    );
                  }
                }
              ]}
              onCancel={closeCollapse}
              onDone={handleSaveOperators}
            />
          )}
        </Paper>
      </Collapse>
    </div>
  );
}

export { ChangeableOperator };
